import React from "react";
import { Skeleton } from "@/components/Skeleton";

/**
 * <DataTable> — one primitive for every list in the app. Enforces 56px
 * rows, tabular numerics, focus rings, hover motion, and a friendly
 * empty state. Never build a raw <table> outside of this component.
 *
 * columns: [
 *   { key, header, render(row), align?: "left"|"right", width?: "w-40", className?, testId? }
 * ]
 * rows: array of records
 * getRowId(row): stable key
 * onRowClick(row): optional — makes rows keyboard-focusable buttons
 */
export default function DataTable({
  columns,
  rows,
  getRowId,
  onRowClick,
  loading = false,
  loadingRows = 5,
  empty = null,   // ReactNode
  compact = false,
  className = "",
  testId,
}) {
  const cellPad = compact ? "py-2.5" : "py-3.5";

  return (
    <div
      data-testid={testId}
      className={`card-pb overflow-hidden ${className}`}
    >
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50/60 dark:bg-slate-900/40 border-b border-[#E2E8F0] dark:border-slate-700">
            <tr>
              {columns.map((c) => (
                <th
                  key={c.key}
                  scope="col"
                  className={`px-4 py-2.5 font-bold text-[10px] uppercase tracking-wider text-[#475569] dark:text-slate-400 ${
                    c.align === "right" ? "text-right" : "text-left"
                  } ${c.width || ""}`}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: loadingRows }).map((_, i) => (
                <tr key={i} className="border-b border-[#E2E8F0] dark:border-slate-800">
                  {columns.map((c, j) => (
                    <td key={j} className={`px-4 ${cellPad}`}>
                      <Skeleton className={`h-3 ${j === 0 ? "w-32" : "w-20"}`} />
                    </td>
                  ))}
                </tr>
              ))
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-4 py-14">
                  {empty || (
                    <p className="text-center text-sm text-[#94A3B8] italic">No records</p>
                  )}
                </td>
              </tr>
            ) : (
              rows.map((row) => {
                const key = getRowId ? getRowId(row) : row.id || row._id || JSON.stringify(row).slice(0, 40);
                const interactive = !!onRowClick;
                return (
                  <tr
                    key={key}
                    onClick={interactive ? () => onRowClick(row) : undefined}
                    onKeyDown={interactive ? (e) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(row); }
                    } : undefined}
                    tabIndex={interactive ? 0 : undefined}
                    role={interactive ? "button" : undefined}
                    className={`border-b border-[#E2E8F0] dark:border-slate-800 last:border-0 transition-colors ${
                      interactive
                        ? "cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/40 focus:outline-none focus-visible:bg-[#C9A356]/8 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#C9A356]"
                        : ""
                    }`}
                  >
                    {columns.map((c) => (
                      <td
                        key={c.key}
                        className={`px-4 ${cellPad} ${
                          c.align === "right" ? "text-right tabular-nums" : ""
                        } ${c.className || ""}`}
                      >
                        {c.render(row)}
                      </td>
                    ))}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** Convenience — client avatar + name + secondary line, used in most tables. */
export function EntityCell({ title, subtitle, initial, avatarTone = "brand" }) {
  const initials = (initial || title || "?").slice(0, 1).toUpperCase();
  const bg =
    avatarTone === "brand" ? "bg-[#C9A356]/15 text-[#8A6B14]" :
    avatarTone === "navy"  ? "bg-[#0A2540]/10 text-[#0A2540] dark:bg-white/10 dark:text-white" :
                             "bg-slate-100 text-slate-600";
  return (
    <div className="flex items-center gap-2.5 min-w-0">
      <div className={`w-8 h-8 rounded-full grid place-items-center text-xs font-bold shrink-0 ${bg}`}>
        {initials}
      </div>
      <div className="min-w-0">
        <div className="text-[#0A2540] dark:text-white truncate font-medium">{title}</div>
        {subtitle ? (
          <div className="text-[11px] text-[#94A3B8] dark:text-slate-500 truncate">{subtitle}</div>
        ) : null}
      </div>
    </div>
  );
}
